import React, { useState, useEffect } from "react";
import {
  isValidE164PhoneNumber,
  FuelNotificationConfig,
  FuelReportConfig,
  AnomalyDetectionConfig,
  AnomalySensitivity,
  AnomalySeverity,
  ANOMALY_SENSITIVITY_THRESHOLDS,
  DEFAULT_ANOMALY_COOLDOWN_MINUTES,
} from "@captiva/shared-types";

import {
  Text,
  MultiSelect,
  Select,
  Switch,
  Stack,
  Input,
  Button,
  Loader,
  Notification,
  Card,
  SimpleGrid,
  Group,
  ActionIcon,
  ThemeIcon,
} from "@mantine/core";

import { collection, getDoc, getDocs, query, where, doc, updateDoc } from "firebase/firestore";

import { db } from "../firebase";
import { Check, Flag, Mail, BrandWhatsapp, FileDescription, Trash, Plus, AlertTriangle } from "tabler-icons-react";
import { PageHeader } from "../components/shared";
import { useFeatureFlags } from "../hooks/useFeatureFlags";

export type NotificationReportConfig = Pick<FuelReportConfig, "reportShortName" | "reportTemplateLink"> & { id: string };

export interface NotificationConfigurationProps {
  companyId: string;
}

const DEFAULT_ANOMALY_CONFIG: AnomalyDetectionConfig = {
  enabled: false,
  sensitivity: "BALANCED",
  minimumSeverityToNotify: "MEDIUM",
  cooldownMinutes: DEFAULT_ANOMALY_COOLDOWN_MINUTES,
  alertOnVisionFailure: false,
};

export function NotificationConfiguration({ companyId }: NotificationConfigurationProps) {
  const { anomalyDetectionEnabled } = useFeatureFlags(companyId);
  const [notificationConfigs, setNotificationConfigs] = useState<Partial<FuelNotificationConfig>>({ emails: [], whatsappNumbers: [] });
  const [loading, setLoading] = useState<boolean>(false);
  const [emailList, setEmailList] = useState<string[]>([]);
  const [whatsappList, setWhatsappList] = useState<string[]>([]);
  const [notificationConfigFetched, setNotificationConfigFetched] = useState<boolean>(false);
  const [captureValue, setCaptureValue] = useState<string[]>([]);
  const [dailyValue, setDailyValue] = useState<string[]>([]);
  const [weeklyValue, setWeeklyValue] = useState<string[]>([]);
  const [monthlyValue, setMonthlyValue] = useState<string[]>([]);
  const [anomalyConfig, setAnomalyConfig] = useState<AnomalyDetectionConfig>(DEFAULT_ANOMALY_CONFIG);
  const [reportConfigs, setReportConfigs] = useState<NotificationReportConfig[]>([]);
  const [reportConfigsFetched, setReportConfigsFetched] = useState<boolean>(false);
  const [nonRangedReportConfigs, setNonRangedReportConfigs] = useState<NotificationReportConfig[]>([]);
  const [message, setMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const sortByName = (entries: NotificationReportConfig[]) => {
    entries.sort((a, b) => {
      const nameA = a.reportShortName || "";
      const nameB = b.reportShortName || "";
      return nameA.localeCompare(nameB);
    });
  };

  useEffect(() => {
    const fetchConfig = async () => {
      setLoading(true);
      const reference = doc(db, 'reportNotifications', companyId);
      const notificationSnapshot = await getDoc(reference);
      if (notificationSnapshot.exists()) {
        setNotificationConfigs(notificationSnapshot.data() as FuelNotificationConfig);
        setLoading(false);
      } else {
        alert(
          "No notification information found. Please contact a CaptivaData representative."
        );
      }
    };

    try {
      if (!notificationConfigFetched && companyId) {
        setNotificationConfigFetched(true);
        fetchConfig();
      }
    } catch (error) {
      console.log(error);
    }
  }, [companyId, notificationConfigFetched]);

  useEffect(() => {
    const buildQuery = (templateName: string) => {
      return query(
        collection(db, templateName),
        where("isActive", "==", true),
        where("isDateRanged", "==", true)
      );
    };
    const buildQueryOnCapture = (templateName: string) => {
      return query(
        collection(db, templateName),
        where("isActive", "==", true),
        where("isDateRanged", "==", false)
      );
    };
    const fetchConfigs = async () => {
      const documentSnapshots = await getDocs(buildQuery("reportSetUp"));
      handleResults(documentSnapshots, setReportConfigs);
    };
    const fetchConfigOnCapture = async () => {
      const documentSnapshots = await getDocs(buildQueryOnCapture("reportSetUp"));
      handleResults(documentSnapshots, setNonRangedReportConfigs);
    };
    const handleResults = (documentSnapshots: any, setter: React.Dispatch<React.SetStateAction<NotificationReportConfig[]>>) => {
      let entries: NotificationReportConfig[] = [];
      documentSnapshots.forEach((docSnap: any) => {
        if (docSnap.get('reportTemplateLink')) {
          entries.push({ id: docSnap.id, ...docSnap.data() } as NotificationReportConfig);
        }
      });
      sortByName(entries);
      setter(entries);
    };
    try {
      if (!reportConfigsFetched) {
        setReportConfigsFetched(true);
        fetchConfigs();
        fetchConfigOnCapture();
      }
    } catch (error) {
      console.log(error);
    }
  }, [reportConfigsFetched]);

  useEffect(() => {
    setEmailList(notificationConfigs.emails || []);
    setWhatsappList(notificationConfigs.whatsappNumbers || []);
    setCaptureValue(notificationConfigs.onCaptured || []);
    setDailyValue(notificationConfigs.daily || []);
    setWeeklyValue(notificationConfigs.weekly || []);
    setMonthlyValue(notificationConfigs.monthly || []);
    if (notificationConfigs.anomalyConfig) {
      setAnomalyConfig({
        ...DEFAULT_ANOMALY_CONFIG,
        ...notificationConfigs.anomalyConfig,
      });
    } else {
      setAnomalyConfig(DEFAULT_ANOMALY_CONFIG);
    }
  }, [notificationConfigs]);

  const addInput = () => {
    setEmailList([...emailList, ""]);
  };

  const removeEmail = (index: number) => {
    const newList = [...emailList];
    newList.splice(index, 1);
    setEmailList(newList);
  };

  const addWhatsappInput = () => {
    setWhatsappList([...whatsappList, ""]);
  };

  const removeWhatsapp = (index: number) => {
    const newList = [...whatsappList];
    newList.splice(index, 1);
    setWhatsappList(newList);
  };

  const saveConfig = async () => {
    const validWhatsappNumbers = whatsappList.filter(w => !!w);
    for (const number of validWhatsappNumbers) {
      if (!isValidE164PhoneNumber(number)) {
        setErrorMessage(`Invalid WhatsApp number: ${number}. Must be in E.164 format (e.g. +27821234567)`);
        return;
      }
    }

    setLoading(true);
    const payload: Record<string, unknown> = {
      onCaptured: captureValue,
      daily: dailyValue,
      weekly: weeklyValue,
      monthly: monthlyValue,
      emails: emailList.filter(e => !!e),
      whatsappNumbers: validWhatsappNumbers,
      hasDaily: dailyValue.length > 0,
      hasWeekly: weeklyValue.length > 0,
      hasMonthly: monthlyValue.length > 0,
    };
    if (anomalyDetectionEnabled) {
      payload.anomalyConfig = anomalyConfig;
    }
    const reference = doc(db, 'reportNotifications', companyId);

    await updateDoc(reference, payload).then(() => {
      setNotificationConfigFetched(false);
      setMessage("Configuration successfully updated.");
    }).catch(() => {
      setErrorMessage('Could not save notification config.');
    });
    setLoading(false);
  };

  return (
    <>
      <PageHeader
        title="Fuel Notifications"
        subtitle="Configure automated email dispatch for daily, weekly, and monthly fuel reports"
        alert={
          (message || errorMessage) ? (
            <>
              {message && (
                <Notification
                  icon={<Check size={18} />}
                  color="teal"
                  title="Success"
                  onClose={() => setMessage("")}
                  style={{ marginBottom: errorMessage ? "0.5em" : 0 }}
                >
                  {message}
                </Notification>
              )}
              {errorMessage && (
                <Notification
                  icon={<Flag size={18} />}
                  color="red"
                  title="Error"
                  onClose={() => setErrorMessage("")}
                >
                  {errorMessage}
                </Notification>
              )}
            </>
          ) : undefined
        }
      />
      {loading ? (
        <Loader style={{ margin: "2em auto", display: "block" }} />
      ) : (
        <div style={{ padding: "0.5em 0", maxWidth: "800px" }}>
          <Card withBorder shadow="sm" radius="md" mb="md">
            <Group mb="sm">
              <ThemeIcon size="md" variant="light" color="blue">
                <Mail size={16} />
              </ThemeIcon>
              <Text size="md" weight={400}>Emails</Text>
            </Group>
            
            <Stack spacing="sm">
              {emailList.map((email, i) => (
                <Group key={`email-${i}`} noWrap>
                  <Input 
                    value={email}
                    size="md"
                    placeholder="email@example.com"
                    style={{ flex: 1 }}
                    onChange={e => {
                      const newList = [...emailList];
                      newList[i] = e.target.value;
                      setEmailList(newList);
                    }}
                  />
                  <ActionIcon color="red" variant="transparent" size="lg" onClick={() => removeEmail(i)} style={{ border: "none", backgroundColor: "transparent" }}>
                    <Trash size={20} color="#fa5252" />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
            <Button 
              mt="md" 
              variant="subtle" 
              color="blue" 
              leftIcon={<Plus size={16} />} 
              onClick={addInput}
            >
              Add Email Address
            </Button>
          </Card>

          <Card withBorder shadow="sm" radius="md" mb="md">
            <Group mb="sm">
              <ThemeIcon size="md" variant="light" color="teal">
                <BrandWhatsapp size={16} />
              </ThemeIcon>
              <Text size="md" weight={400}>WhatsApp Numbers</Text>
            </Group>

            <Stack spacing="sm">
              {whatsappList.map((number, i) => (
                <Group key={`wa-${i}`} noWrap>
                  <Input
                    value={number}
                    size="md"
                    placeholder="+27821234567"
                    style={{ flex: 1 }}
                    onChange={e => {
                      const newList = [...whatsappList];
                      newList[i] = e.target.value;
                      setWhatsappList(newList);
                    }}
                  />
                  <ActionIcon color="red" variant="transparent" size="lg" onClick={() => removeWhatsapp(i)} style={{ border: "none", backgroundColor: "transparent" }}>
                    <Trash size={20} color="#fa5252" />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
            <Button 
              mt="md" 
              variant="subtle" 
              color="teal" 
              leftIcon={<Plus size={16} />} 
              onClick={addWhatsappInput}
            >
              Add WhatsApp Number
            </Button>
          </Card>

          <Card withBorder shadow="sm" radius="md" mb="md">
            <Group mb="sm">
              <ThemeIcon size="md" variant="light" color="grape">
                <FileDescription size={16} />
              </ThemeIcon>
              <Text size="md" weight={400}>Report Triggers</Text>
            </Group>

            <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]} spacing="sm">
              <MultiSelect 
                label="Reports on capture"
                placeholder="Select reports"
                searchable
                clearable
                nothingFound="No matches found"
                value={captureValue} 
                onChange={setCaptureValue} 
                data={nonRangedReportConfigs.map((config) => {
                  return { value: config.id, label: config.reportShortName };
                })} 
              />
              <MultiSelect 
                label="Daily reports"
                placeholder="Select reports"
                searchable
                clearable
                nothingFound="No matches found"
                value={dailyValue} 
                onChange={setDailyValue} 
                data={reportConfigs.map((config) => {
                  return { value: config.id, label: config.reportShortName };
                })} 
              />
              <MultiSelect 
                label="Weekly reports"
                placeholder="Select reports"
                searchable
                clearable
                nothingFound="No matches found"
                value={weeklyValue}
                onChange={setWeeklyValue} 
                data={reportConfigs.map((config) => {
                  return { value: config.id, label: config.reportShortName };
                })} 
              />
              <MultiSelect 
                label="Monthly reports"
                placeholder="Select reports"
                searchable
                clearable
                nothingFound="No matches found"
                value={monthlyValue} 
                onChange={setMonthlyValue} 
                data={reportConfigs.map((config) => {
                  return { value: config.id, label: config.reportShortName };
                })} 
              />
            </SimpleGrid>
          </Card>

          {anomalyDetectionEnabled && (
            <Card withBorder shadow="sm" radius="md" mb="md">
              <Group position="apart" mb="sm">
                <Group>
                  <ThemeIcon size="md" variant="light" color="orange">
                    <AlertTriangle size={16} />
                  </ThemeIcon>
                  <div>
                    <Text size="md" weight={500}>Real-Time AI Anomaly &amp; Spike Alerts</Text>
                    <Text size="xs" color="dimmed">
                      Automatically evaluate fleet fuel burn rates (L/hr &amp; L/100km), tank mass-balance discrepancies, and meter overflows against historical baselines
                    </Text>
                  </div>
                </Group>
                <Switch
                  checked={anomalyConfig.enabled}
                  onChange={(event) =>
                    setAnomalyConfig({
                      ...anomalyConfig,
                      enabled: event.currentTarget.checked,
                    })
                  }
                  label={anomalyConfig.enabled ? "Enabled" : "Disabled"}
                  color="orange"
                />
              </Group>

              {anomalyConfig.enabled && (
                <Stack spacing="sm" mt="md">
                  <SimpleGrid cols={2} breakpoints={[{ maxWidth: "sm", cols: 1 }]} spacing="sm">
                    <Select
                      label="Detection Sensitivity"
                      description={ANOMALY_SENSITIVITY_THRESHOLDS[anomalyConfig.sensitivity || "BALANCED"]?.description}
                      value={anomalyConfig.sensitivity || "BALANCED"}
                      onChange={(val) =>
                        val &&
                        setAnomalyConfig({
                          ...anomalyConfig,
                          sensitivity: val as AnomalySensitivity,
                        })
                      }
                      data={[
                        { value: "CONSERVATIVE", label: "Conservative (2.5x baseline / 3.5σ)" },
                        { value: "BALANCED", label: "Balanced — Recommended (2.0x baseline / 3.0σ)" },
                        { value: "SENSITIVE", label: "Sensitive (1.6x baseline / 2.5σ)" },
                      ]}
                    />
                    <Select
                      label="Minimum Alert Severity"
                      description="Only dispatch Email & WhatsApp alerts at or above this severity"
                      value={anomalyConfig.minimumSeverityToNotify || "MEDIUM"}
                      onChange={(val) =>
                        val &&
                        setAnomalyConfig({
                          ...anomalyConfig,
                          minimumSeverityToNotify: val as Exclude<AnomalySeverity, "NONE">,
                        })
                      }
                      data={[
                        { value: "MEDIUM", label: "Medium & Above (2.0x+ spikes & reconciliations)" },
                        { value: "HIGH", label: "High & Critical Only (major spikes & tank discrepancies)" },
                        { value: "CRITICAL", label: "Critical Only (severe loss / overflow)" },
                      ]}
                    />
                  </SimpleGrid>

                  <Switch
                    mt="xs"
                    checked={!!anomalyConfig.alertOnVisionFailure}
                    onChange={(event) =>
                      setAnomalyConfig({
                        ...anomalyConfig,
                        alertOnVisionFailure: event.currentTarget.checked,
                      })
                    }
                    label="Also trigger an anomaly evaluation when AI Photo Verification detects a meter or slip mismatch"
                  />
                </Stack>
              )}
            </Card>
          )}

          <div style={{ position: 'sticky', bottom: 0, padding: '1rem 0', backgroundColor: 'var(--mantine-color-body, white)', borderTop: '1px solid #eaeaea', zIndex: 10, marginTop: 'auto' }}>
            <Button size="md" color="cap-navy" onClick={saveConfig} style={{ minWidth: 150 }}>
              Save
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default NotificationConfiguration;